/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Scissors } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import axiosClient from '../../api/axiosClient';
import { useTheme } from '../../context/ThemeContext';
import { formatAiProviderLabel } from '../../utils/aiProvider';
import { buildAiPromptSummary } from '../../utils/aiPromptSummary';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component (aligned with other admin pages)
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      className
    }
  >
    {children}
  </div>
);

// Add custom styles to hide scrollbar but keep functionality
const styles = `
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('http') || imagePath.startsWith('data:')) return imagePath;

  const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;

  let backendOrigin = window.location.origin;
  try {
    const base = axiosClient?.defaults?.baseURL || '';
    if (base) backendOrigin = new URL(base, window.location.origin).origin;
  } catch (_) {
    // ignore
  }

  return `${backendOrigin}${normalizedPath}`;
};

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const getOriginalPath = (scan) => scan?.original_image_path || scan?.image_url || '';

const getGeneratedPaths = (scan) => {
  if (!scan) return [];
  if (Array.isArray(scan.makeup_result_urls) && scan.makeup_result_urls.length > 0) return scan.makeup_result_urls;
  const styled = scan.hairstyle_suggestions?.styledImages;
  if (Array.isArray(styled) && styled.length > 0) return styled;
  if (scan.result_url) return [scan.result_url];
  return [];
};

const getRecordTypeLabel = (scan) => {
  if (!scan) return 'AI Result';
  if (Array.isArray(scan.makeup_result_urls) && scan.makeup_result_urls.length > 0) return 'Makeup';
  const method = scan.hairstyle_suggestions?.method;
  if (method === 'nails') return 'Nails';
  if (method === 'makeup') return 'Makeup';
  return 'Hairstyles';
};

const ImageTile = ({ src, alt, className = '', fit = 'cover', position = 'center', mobilefit = null }) => {
  // Build responsive classes: mobile uses mobilefit (if provided), desktop uses fit
  let objectFitClasses = '';
  if (mobilefit) {
    // Mobile: use mobilefit, Desktop: use fit
    const mobileClass = mobilefit === 'contain' ? 'object-contain' : 'object-cover';
    const desktopClass = fit === 'contain' ? 'md:object-contain' : 'md:object-cover';
    objectFitClasses = `${mobileClass} ${desktopClass}`;
  } else {
    // No mobilefit specified, use fit for all breakpoints
    objectFitClasses = fit === 'contain' ? 'object-contain' : 'object-cover';
  }
  
  const positionClass = position === 'top' ? 'object-top' : 'object-center';
  
  return (
    <div className={'w-full h-full rounded-2xl overflow-hidden bg-zinc-100 dark:bg-zinc-800/60 border border-white/10 dark:border-white/10 ' + className}>
      {src ? (
        <img
          src={src}
          alt={alt}
          className={`w-full h-full ${objectFitClasses} ${positionClass}`}
          loading="eager"
          decoding="async"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-zinc-400">
          <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}
    </div>
  );
};

const ClientAIAnalysisForClientPage = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { clientId } = useParams();
  const detailsRef = useRef(null);
  const sliderRef = useRef(null);

  const [client, setClient] = useState(null);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScanId, setSelectedScanId] = useState(null);
  const [selectedGeneratedIndex, setSelectedGeneratedIndex] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const loadClientAndScans = async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      
      // First, fetch client details (validates access and gets name)
      const clientRes = await axiosClient.get(`/admin/clients/${clientId}`);
      const clientData = clientRes.data.data || clientRes.data;
      setClient(clientData);
      
      // Then fetch scans using client name (backend expects name)
      const clientName = clientData.name;
      const scansRes = await axiosClient.get(`/scans/by-client/${encodeURIComponent(clientName)}`);
      const next = Array.isArray(scansRes.data) ? scansRes.data : [];
      next.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setScans(next);
      
      // If current selection was deleted, select first
      setSelectedScanId(prev => {
        if (prev && next.find(s => s.id === prev)) return prev;
        return next[0]?.id ?? null;
      });
      setSelectedGeneratedIndex(0);
    } catch (e) {
      console.error('Failed to load client data:', e);
      if (e.response?.status === 404) {
        toast.error('Client not found');
      } else if (e.response?.status === 403) {
        toast.error('Access denied');
      } else {
        toast.error('Failed to load AI analysis');
      }
      setScans([]);
      setSelectedScanId(null);
      setClient(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClientAndScans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const handleDeleteScan = (scanId) => {
    const scan = scans.find(s => s.id === scanId);
    setConfirmData({
      title: 'Delete Scan',
      message: 'Are you sure you want to permanently delete this scan and all associated images?',
      details: [
        `Scan #${scanId}`,
        `Client: ${client?.name || 'Unknown'}`
      ],
      warning: 'This action cannot be undone!',
      onConfirm: () => performDeleteScan(scanId)
    });
    setShowConfirmModal(true);
  };

  const performDeleteScan = async (scanId) => {
    try {
      toast.loading('Deleting...', { id: 'delete' });
      await axiosClient.delete(`/scans/${scanId}`);
      toast.success('Deleted successfully', { id: 'delete' });
      // Remove from local state
      setScans(prev => {
        const next = prev.filter(s => s.id !== scanId);
        // If we deleted the selected one, pick the first remaining
        if (selectedScanId === scanId) {
          setSelectedScanId(next[0]?.id ?? null);
        }
        return next;
      });
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete. Please try again.', { id: 'delete' });
    }
  };

  const handleDownloadImage = async (imageUrl, scanId) => {
    try {
      toast.loading('Preparing download...', { id: 'download' });
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error('Failed to fetch image');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `generated-${scanId}-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded', { id: 'download' });
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download failed', { id: 'download' });
    }
  };

  const selectedScan = useMemo(() => scans.find((s) => s.id === selectedScanId) || null, [scans, selectedScanId]);
  const originalUrl = useMemo(() => getImageUrl(getOriginalPath(selectedScan)), [selectedScan]);
  const generatedPaths = useMemo(() => getGeneratedPaths(selectedScan), [selectedScan]);
  const generatedUrl = useMemo(() => {
    const path = generatedPaths[selectedGeneratedIndex] || generatedPaths[0] || '';
    return getImageUrl(path);
  }, [generatedPaths, selectedGeneratedIndex]);
  const promptSummary = useMemo(() => buildAiPromptSummary(selectedScan), [selectedScan]);

  // Check scroll position for navigation arrows
  const checkScroll = () => {
    if (sliderRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('scroll', checkScroll);
      return () => slider.removeEventListener('scroll', checkScroll);
    }
  }, [scans]);

  const scrollSlider = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = sliderRef.current.clientWidth * 0.8;
      sliderRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="relative min-h-screen">
      <style>{styles}</style>
      {/* Dark-only background accents (subtle, cleaner than heavy glass) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden dark:block">
        <div className="absolute -top-10 -left-10 w-96 h-96 bg-gradient-to-br from-[#7d6633]/18 to-[#5a4d26]/12 rounded-full blur-3xl" />
        <div className="absolute top-24 -right-16 w-80 h-80 bg-gradient-to-br from-[#a0824a]/14 to-[#7d6633]/18 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-1/3 w-72 h-72 bg-gradient-to-br from-[#d4b47a]/10 to-[#a0824a]/14 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 space-y-5">
        {/* Header */}
        <Card>
          <div className="p-4 md:p-5 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/admin/clients')}
                  className="px-3 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-200 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                >
                  Back
                </motion.button>
                <h1 className="text-xl md:text-2xl font-bold text-zinc-900 dark:text-zinc-100 truncate">
                  AI Analysis • {client?.name || 'Loading...'}
                </h1>
              </div>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
                Browse this client’s AI results with a cleaner, full-page layout.
              </p>
            </div>

            <div className="hidden md:flex items-center gap-2">
              <span
                className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-[--accent]/10 text-zinc-800 dark:text-zinc-100 border border-white/10 dark:border-white/10"
                style={{ '--accent': ACCENT }}
              >
                {loading ? 'Loading…' : `${scans.length} record${scans.length === 1 ? '' : 's'}`}
              </span>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-5">
          {/* Left: list */}
          <Card className="overflow-hidden xl:sticky xl:top-4 xl:flex xl:flex-col xl:max-h-[calc(100vh-140px)]">
            <div className="p-4 border-b border-white/10 dark:border-white/10">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Records</h2>
                <div className="flex items-center gap-2">
                  {/* Tablet/mobile scroll arrows */}
                  <div className="xl:hidden flex items-center gap-1">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => scrollSlider('left')}
                      disabled={!canScrollLeft}
                      className="p-1.5 rounded-lg border border-white/20 dark:border-white/10 bg-white/40 dark:bg-zinc-800/40 text-zinc-700 dark:text-zinc-200 hover:bg-white/60 dark:hover:bg-zinc-800/60 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => scrollSlider('right')}
                      disabled={!canScrollRight}
                      className="p-1.5 rounded-lg border border-white/20 dark:border-white/10 bg-white/40 dark:bg-zinc-800/40 text-zinc-700 dark:text-zinc-200 hover:bg-white/60 dark:hover:bg-zinc-800/60 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </motion.button>
                  </div>
                  <button
                    onClick={loadClientAndScans}
                    className="text-xs px-2.5 py-1.5 rounded-lg border border-white/20 dark:border-white/10 bg-white/40 dark:bg-zinc-800/40 backdrop-blur-sm text-zinc-700 dark:text-zinc-200 hover:bg-white/60 dark:hover:bg-zinc-800/60 transition-all"
                  >
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            <div className="p-3 space-y-2 xl:flex-1 xl:overflow-y-auto">
              {loading ? (
                <div className="space-y-2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="rounded-xl border border-white/10 dark:border-white/10 bg-white/40 dark:bg-zinc-800/40 p-3">
                      <div className="flex gap-3">
                        <div className="w-16 h-16 rounded-xl bg-zinc-200 dark:bg-zinc-700 animate-pulse" />
                        <div className="w-16 h-16 rounded-xl bg-zinc-200 dark:bg-zinc-700 animate-pulse" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3 bg-zinc-200 dark:bg-zinc-700 rounded animate-pulse w-2/3" />
                          <div className="h-3 bg-zinc-200 dark:bg-zinc-700 rounded animate-pulse w-1/2" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : scans.length === 0 ? (
                <div className="p-6 text-center text-zinc-600 dark:text-zinc-400">
                  <div className="mb-2 text-sm font-medium">No AI Analysis Found</div>
                  <div className="text-xs">Generate an AI result for this client to see it here.</div>
                </div>
              ) : (
                <>
                  {/* Tablet/mobile: modern horizontal carousel with arrows */}
                  <div className="xl:hidden -mx-3 px-3 relative">
                    {/* Side arrows (overlay) */}
                    <div className="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center justify-between z-20">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => scrollSlider('left')}
                        disabled={!canScrollLeft}
                        className="pointer-events-auto ml-1 p-2 rounded-full border border-white/20 dark:border-white/10 bg-white/60 dark:bg-zinc-900/60 backdrop-blur text-zinc-700 dark:text-zinc-200 shadow-lg transition-all disabled:opacity-20 disabled:cursor-not-allowed"
                        aria-label="Scroll records left"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => scrollSlider('right')}
                        disabled={!canScrollRight}
                        className="pointer-events-auto mr-1 p-2 rounded-full border border-white/20 dark:border-white/10 bg-white/60 dark:bg-zinc-900/60 backdrop-blur text-zinc-700 dark:text-zinc-200 shadow-lg transition-all disabled:opacity-20 disabled:cursor-not-allowed"
                        aria-label="Scroll records right"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </motion.button>
                    </div>

                    <div 
                      ref={sliderRef}
                      className="overflow-x-auto scroll-smooth snap-x snap-mandatory hide-scrollbar"
                      onScroll={checkScroll}
                    >
                      <div className="flex gap-3 pb-2">
                        {scans.map((scan) => {
                          const isActive = scan.id === selectedScanId;
                          const generated = getImageUrl(getGeneratedPaths(scan)[0] || '');
                          const suggestions = scan.hairstyle_suggestions || {};
                          return (
                            <div
                              key={scan.id}
                              className={
                                // More square aspect ratio, smaller cards
                                'relative flex-none shrink-0 snap-start w-[60vw] max-w-[200px] text-left rounded-2xl border transition-all overflow-hidden ' +
                                (isActive
                                  ? 'border-[--accent]/50 ring-2 ring-[--accent]/15 bg-[--accent]/10'
                                  : 'border-white/10 dark:border-white/10 bg-white/40 dark:bg-zinc-800/40')
                              }
                              style={{ '--accent': ACCENT }}
                            >
                              <button
                                onClick={() => {
                                  setSelectedScanId(scan.id);
                                  setSelectedGeneratedIndex(0);
                                  setTimeout(() => {
                                    detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }, 50);
                                }}
                                className="w-full"
                              >
                                <div className="aspect-square">
                                  <ImageTile src={generated} alt="Generated" className="rounded-none border-0" fit="cover" position="center" />
                                </div>
                                <div className="p-2.5">
                                  <div className="text-xs font-semibold text-zinc-900 dark:text-zinc-100 truncate">
                                    #{scan.id} • {getRecordTypeLabel(scan)}
                                  </div>
                                  <div className="text-[10px] text-zinc-600 dark:text-zinc-400 mt-1 truncate">
                                    {formatDate(scan.created_at)}
                                  </div>
                                  {suggestions.faceShape && (
                                    <div className="mt-1.5">
                                      <span className="inline-flex text-[9px] px-1.5 py-0.5 rounded-full bg-white/70 dark:bg-zinc-900/50 border border-white/10 dark:border-white/10 text-zinc-700 dark:text-zinc-200">
                                        {suggestions.faceShape}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </button>
                              {/* Delete button */}
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteScan(scan.id);
                                }}
                                className="absolute top-2 right-2 p-1 rounded-full bg-red-500/90 hover:bg-red-600 text-white shadow-lg transition-colors z-10"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </motion.button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    
                  {/* Per-card scroll indicators (scissors) */}
                    {scans.length > 1 && (
                      <div className="mt-2 -mx-3 px-3">
                        <div className="flex items-center justify-center gap-1.5 overflow-x-auto hide-scrollbar py-1">
                          {scans.map((scan, idx) => {
                            const isActive = scan.id === selectedScanId;
                            return (
                              <button
                                key={scan.id}
                                type="button"
                                onClick={() => {
                                  const slider = sliderRef.current;
                                  if (slider) {
                                    const cardWidth = slider.scrollWidth / scans.length;
                                    slider.scrollTo({ left: cardWidth * idx, behavior: 'smooth' });
                                  }
                                }}
                                className={[
                                  'p-1.5 md:p-2 rounded-full transition-all',
                                  isActive
                                    ? 'bg-[--accent]/15 text-[--accent]'
                                    : 'text-zinc-400 dark:text-zinc-500 hover:text-zinc-500 dark:hover:text-zinc-300',
                                ].join(' ')}
                                style={{ '--accent': ACCENT }}
                                aria-label={`Go to record ${idx + 1}`}
                                aria-current={isActive ? 'true' : 'false'}
                              >
                                <Scissors className="h-4 w-4 md:h-5 md:w-5" />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Desktop: full list (show generated as first thumbnail) */}
                  <div className="hidden xl:block space-y-2">
                    {scans.map((scan) => {
                      const isActive = scan.id === selectedScanId;
                      const generated = getImageUrl(getGeneratedPaths(scan)[0] || '');
                      const original = getImageUrl(getOriginalPath(scan));
                      const suggestions = scan.hairstyle_suggestions || {};

                      return (
                        <div
                          key={scan.id}
                          className={
                            'relative w-full text-left rounded-xl border transition-all p-3 ' +
                            (isActive
                              ? 'border-[--accent]/40 bg-[--accent]/10'
                              : 'border-white/10 dark:border-white/10 bg-white/40 dark:bg-zinc-800/40 hover:bg-white/60 dark:hover:bg-zinc-800/60')
                          }
                          style={{ '--accent': ACCENT }}
                        >
                          <button
                            onClick={() => {
                              setSelectedScanId(scan.id);
                              setSelectedGeneratedIndex(0);
                            }}
                            className="w-full text-left"
                          >
                            <div className="flex gap-3">
                              <div className="w-16 h-16">
                                <ImageTile src={generated} alt="Generated" className="rounded-xl" />
                              </div>
                              <div className="w-16 h-16">
                                <ImageTile src={original} alt="Original" className="rounded-xl" />
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">
                                    #{scan.id} • {getRecordTypeLabel(scan)}
                                  </div>
                                  {suggestions.faceShape && (
                                    <span className="shrink-0 text-[10px] px-2 py-1 rounded-full bg-white/70 dark:bg-zinc-900/50 border border-white/10 dark:border-white/10 text-zinc-700 dark:text-zinc-200">
                                      {suggestions.faceShape}
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">
                                  {formatDate(scan.created_at)}
                                </div>
                              </div>
                            </div>
                          </button>
                          {/* Delete button */}
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScan(scan.id);
                            }}
                            className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500/90 hover:bg-red-600 text-white shadow-lg transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </motion.button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Right: details (no popup) */}
          <Card className="overflow-hidden" ref={detailsRef}>
            <div className="p-4 md:p-5 border-b border-white/10 dark:border-white/10">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                    {selectedScan ? `Analysis #${selectedScan.id}` : 'Select a record'}
                  </h2>
                  {selectedScan && (
                    <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-0.5">
                      {getRecordTypeLabel(selectedScan)} • {formatDate(selectedScan.created_at)}
                    </p>
                  )}
                </div>
                {(selectedScan?.hairstyle_suggestions?.provider || selectedScan?.provider) && (
                  <span className="text-xs px-2.5 py-1.5 rounded-full bg-white/60 dark:bg-zinc-900/40 border border-white/10 dark:border-white/10 text-zinc-700 dark:text-zinc-200">
                    {formatAiProviderLabel(selectedScan?.hairstyle_suggestions?.provider || selectedScan?.provider)}
                  </span>
                )}
              </div>
            </div>

            {!selectedScan ? (
              <div className="p-8 text-center text-zinc-600 dark:text-zinc-400">
                Pick a record from the left to see details here.
              </div>
            ) : (
              <div className="p-4 md:p-5 space-y-5">
                {/* Big images: object-cover on mobile (full square), object-contain on desktop */}
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <div className="flex items-center justify-between h-8 mb-2">
                      <div className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Original</div>
                    </div>
                    <div className="h-[min(42vh,360px)] md:h-[min(62vh,540px)]">
                      <ImageTile src={originalUrl} alt="Original" fit="contain" position="center" mobilefit="cover" />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between h-8 mb-2">
                      <div className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Generated</div>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleDownloadImage(generatedUrl, selectedScan.id)}
                        className="text-xs px-2.5 py-1 rounded-lg border border-white/20 dark:border-white/10 bg-white/40 dark:bg-zinc-800/40 text-zinc-700 dark:text-zinc-200 hover:bg-white/60 dark:hover:bg-zinc-800/60 transition-all flex items-center gap-1"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download
                      </motion.button>
                    </div>
                    <div className="h-[min(42vh,360px)] md:h-[min(62vh,540px)]">
                      <ImageTile src={generatedUrl} alt="Generated" fit="contain" position="center" mobilefit="cover" />
                    </div>
                  </div>
                </div>

                {/* Generated selector */}
                {generatedPaths.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {generatedPaths.slice(0, 12).map((p, idx) => {
                      const url = getImageUrl(p);
                      const active = idx === selectedGeneratedIndex;
                      return (
                        <button
                          key={`${selectedScan.id}-${idx}`}
                          onClick={() => setSelectedGeneratedIndex(idx)}
                          className={
                            'w-16 h-16 rounded-xl overflow-hidden border transition-all ' +
                            (active
                              ? 'border-[--accent]/60 ring-2 ring-[--accent]/20'
                              : 'border-white/10 dark:border-white/10 hover:border-white/20 dark:hover:border-white/20')
                          }
                          style={{ '--accent': ACCENT }}
                          title={`Generated ${idx + 1}`}
                        >
                          <img src={url} alt={`Generated ${idx + 1}`} className="w-full h-full object-cover" />
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Info */}
                <div className="rounded-2xl border border-white/10 dark:border-white/10 bg-white/40 dark:bg-zinc-800/30 backdrop-blur-sm p-4">
                  <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-3">Analysis Information</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="text-zinc-700 dark:text-zinc-300">
                      <span className="font-medium text-zinc-600 dark:text-zinc-400">Type:</span>{' '}
                      {getRecordTypeLabel(selectedScan)}
                    </div>
                    {(selectedScan.hairstyle_suggestions?.provider || selectedScan.provider) && (
                      <div className="text-zinc-700 dark:text-zinc-300">
                        <span className="font-medium text-zinc-600 dark:text-zinc-400">Provider:</span>{' '}
                        {formatAiProviderLabel(selectedScan.hairstyle_suggestions?.provider || selectedScan.provider)}
                      </div>
                    )}
                    {selectedScan.hairstyle_suggestions?.method && (
                      <div className="text-zinc-700 dark:text-zinc-300">
                        <span className="font-medium text-zinc-600 dark:text-zinc-400">Method:</span>{' '}
                        {selectedScan.hairstyle_suggestions.method}
                      </div>
                    )}
                    {selectedScan.hairstyle_suggestions?.faceShape && (
                      <div className="text-zinc-700 dark:text-zinc-300">
                        <span className="font-medium text-zinc-600 dark:text-zinc-400">Face shape:</span>{' '}
                        {selectedScan.hairstyle_suggestions.faceShape}
                      </div>
                    )}
                    {selectedScan.haircut && (
                      <div className="text-zinc-700 dark:text-zinc-300">
                        <span className="font-medium text-zinc-600 dark:text-zinc-400">Style:</span> {selectedScan.haircut}
                      </div>
                    )}
                    {selectedScan.hair_color && (
                      <div className="text-zinc-700 dark:text-zinc-300">
                        <span className="font-medium text-zinc-600 dark:text-zinc-400">Color:</span> {selectedScan.hair_color}
                      </div>
                    )}
                    {!!selectedScan.credits_used && (
                      <div className="text-zinc-700 dark:text-zinc-300">
                        <span className="font-medium text-zinc-600 dark:text-zinc-400">Credits:</span> {selectedScan.credits_used}
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 dark:border-white/10 bg-white/40 dark:bg-zinc-800/30 backdrop-blur-sm p-4">
                  <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-3">AI Prompt</div>
                  <div className="space-y-4 text-sm text-zinc-700 dark:text-zinc-300">
                    <div>
                      <span className="font-medium text-zinc-600 dark:text-zinc-400">Hairstyle:</span>{' '}
                      {promptSummary.hairstyle}
                    </div>
                    <div>
                      <span className="font-medium text-zinc-600 dark:text-zinc-400">Face Shape:</span>{' '}
                      {promptSummary.faceShape}
                    </div>
                    <div>
                      <div className="font-medium text-zinc-600 dark:text-zinc-400 mb-2">Hair Profile:</div>
                      <div className="space-y-1 pl-3">
                        <div>Curl type: {promptSummary.hairProfile.curlType}</div>
                        <div>Density: {promptSummary.hairProfile.density}</div>
                        <div>Thickness: {promptSummary.hairProfile.thickness}</div>
                        <div>Length: {promptSummary.hairProfile.length}</div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            )}
          </Card>
        </div>

        {/* Confirmation Modal (delete scan) */}
        {showConfirmModal && confirmData && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="p-6 rounded-2xl shadow-2xl max-w-md w-full mx-4 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10"
            >
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mr-3">
                  <svg className="h-5 w-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">
                  {confirmData.title}
                </h3>
              </div>

              <div className="mb-6">
                <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-3">
                  {confirmData.message}
                </p>
                {confirmData.details && (
                  <div className="bg-zinc-100 dark:bg-zinc-800/50 p-4 rounded-xl text-sm border border-white/10 dark:border-white/10">
                    {confirmData.details.map((detail, index) => (
                      <div key={index} className="text-zinc-700 dark:text-zinc-300">{detail}</div>
                    ))}
                  </div>
                )}
                {confirmData.warning && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-3 font-medium">
                    {confirmData.warning}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setShowConfirmModal(false);
                    confirmData.onConfirm();
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all shadow-lg"
                >
                  Confirm
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientAIAnalysisForClientPage;

